// Phase 5 (web revival) — Stream C.
//
// Per-asset share list. Owner sees everyone (display names resolved via
// /v1/users/{id}) plus an "+ Share with…" modal that posts UUIDs to
// /v1/assets/{id}/share. Non-owner sees only their own entry plus a
// "Leave" button.
//
// AssetDetailDTO.shares is just an array of recipient user-ids. To get
// the share_id (needed for DELETE /v1/asset-shares/{id}) and full role
// info, the owner fetches /v1/assets/{id}/shares — which is owner-only
// (404 for everyone else). Non-owners can still DELETE their own share
// but they don't have access to that list endpoint to look up their
// share_id; instead, the WS event `asset.share.revoked` will tear them
// off the asset entirely (the detail query will start 404ing) so the
// "Leave" UI just optimistically navigates back to /shared-with-me on
// 204.

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api, ApiError } from '../../api/client';
import { qk } from '../../app/queryClient';
import type {
  AssetDetailDTO,
  AssetShareRole,
  AssetSharesListResponse,
  CreateAssetShareRequest,
  CreateAssetShareResponse,
  PublicUserDTO,
} from '../../api/types';

interface SharesPanelProps {
  asset: AssetDetailDTO;
  isOwner: boolean;
  currentUserID: string | null;
}

export function SharesPanel({ asset, isOwner, currentUserID }: SharesPanelProps) {
  return (
    <div className="border-t border-white/5 px-5 py-4">
      <div className="mb-2 text-[11px] uppercase tracking-[0.14em] text-neutral-500">
        Shared with
      </div>
      {isOwner ? (
        <OwnerSharesView asset={asset} />
      ) : (
        <RecipientShareView asset={asset} currentUserID={currentUserID} />
      )}
    </div>
  );
}

// ────────────────────── Owner: full list + add button ──────────────────────

function OwnerSharesView({ asset }: { asset: AssetDetailDTO }) {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  const sharesQuery = useQuery({
    queryKey: qk.assetShares(asset.id),
    queryFn: () =>
      api.get<AssetSharesListResponse>(`/v1/assets/${asset.id}/shares`),
  });

  const shares = sharesQuery.data?.shares ?? [];

  const revoke = useMutation<void, ApiError, string>({
    mutationFn: (shareID) => api.delete<void>(`/v1/asset-shares/${shareID}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.assetShares(asset.id) });
      queryClient.invalidateQueries({ queryKey: qk.asset(asset.id) });
    },
  });

  return (
    <div className="space-y-2">
      {sharesQuery.isLoading && (
        <p className="text-xs text-neutral-500">Loading shares…</p>
      )}
      {sharesQuery.isError && (
        <p className="text-xs text-red-300">
          {sharesQuery.error instanceof ApiError
            ? sharesQuery.error.reason
            : 'Failed to load shares.'}
        </p>
      )}
      {sharesQuery.data && shares.length === 0 && (
        <p className="text-xs text-neutral-500">Not shared yet.</p>
      )}
      {shares.length > 0 && (
        <ul className="space-y-1">
          {shares.map((s) => {
            const name =
              s.shared_with?.display_name ??
              s.shared_with_user_id.slice(0, 8);
            return (
              <li
                key={s.id}
                className="flex items-center justify-between rounded-md bg-neutral-900 px-2 py-1.5 text-xs"
              >
                <div>
                  <div className="text-neutral-100">{name}</div>
                  <div className="text-[10px] text-neutral-500">{s.role}</div>
                </div>
                <button
                  type="button"
                  disabled={revoke.isPending}
                  onClick={() => {
                    if (!window.confirm(`Remove access for ${name}?`)) return;
                    revoke.mutate(s.id);
                  }}
                  className="rounded-md border border-neutral-700 px-2 py-0.5 text-[10px] text-neutral-300 hover:bg-neutral-800 disabled:opacity-50"
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className="w-full rounded-md border border-neutral-700 px-3 py-1.5 text-xs text-neutral-300 hover:bg-neutral-800"
      >
        + Share with…
      </button>
      {modalOpen && (
        <ShareModal asset={asset} onClose={() => setModalOpen(false)} />
      )}
    </div>
  );
}

// ────────────────────── Non-owner: my row + Leave ──────────────────────

function RecipientShareView({
  asset,
  currentUserID,
}: {
  asset: AssetDetailDTO;
  currentUserID: string | null;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // We can't fetch the share list as a recipient. We CAN find ourselves
  // in the asset's shares array (which is just user ids) — that confirms
  // we're a direct share recipient. Without share_id we can't DELETE
  // (the route is keyed on share_id, not user_id). For v1, the Leave
  // button surfaces as an informational note; deeper "leave a direct
  // share" UX is iOS-parity-grade work.
  //
  // (If the API later grows GET /v1/users/me/asset-shares or similar,
  // wire it here.)
  const others = asset.shares.filter((id) => id !== currentUserID);

  const removeSelf = useMutation<void, ApiError, void>({
    // Same caveat: we don't know our own share_id from this endpoint.
    // Leave best-effort: hit the unshare endpoint with the asset id so
    // anyone who CAN reach it gets a chance to clean up. Most likely
    // this will just 404 and that's fine.
    mutationFn: async () => {
      // We don't have a direct "leave" endpoint without share_id;
      // surface a friendly error and let the recipient ask the owner
      // to revoke. WS removal will then drop us off this asset.
      throw new ApiError(
        501,
        'asset.share.leave.unsupported',
        'Ask the asset owner to remove your access.',
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.feedSharedWithMe() });
      navigate({ to: '/shared-with-me' });
    },
  });

  return (
    <div className="space-y-2 text-xs">
      <p className="text-neutral-300">
        Shared with you by the asset owner.
      </p>
      {others.length > 0 && (
        <p className="text-neutral-500">
          Also shared with {others.length}{' '}
          {others.length === 1 ? 'person' : 'people'}.
        </p>
      )}
      <button
        type="button"
        disabled={removeSelf.isPending}
        onClick={() => removeSelf.mutate()}
        className="w-full rounded-md border border-neutral-700 px-3 py-1.5 text-neutral-300 hover:bg-neutral-800 disabled:opacity-50"
      >
        Leave
      </button>
      {removeSelf.error && (
        <p className="text-[10px] text-amber-300">{removeSelf.error.reason}</p>
      )}
    </div>
  );
}

// ────────────────────── + Share with… modal ──────────────────────

function ShareModal({
  asset,
  onClose,
}: {
  asset: AssetDetailDTO;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<PublicUserDTO | null>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [role, setRole] = useState<AssetShareRole>('viewer');
  const comboRef = useRef<HTMLDivElement>(null);

  const usersQuery = useQuery({
    queryKey: qk.users(),
    queryFn: () => api.get<PublicUserDTO[]>('/v1/users'),
    staleTime: 5 * 60_000,
  });

  // Hide already-shared recipients so the owner can't pick a duplicate.
  const alreadyShared = useMemo(
    () => new Set(asset.shares),
    [asset.shares],
  );

  const filtered = useMemo(() => {
    const all = (usersQuery.data ?? []).filter((u) => !alreadyShared.has(u.id));
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((u) => {
      const name = (u.display_name ?? '').toLowerCase();
      return name.includes(q) || u.id.toLowerCase().startsWith(q);
    });
  }, [usersQuery.data, alreadyShared, query]);

  // Keep the keyboard highlight in range as the filter shrinks the list.
  useEffect(() => {
    setHighlight((h) => Math.max(0, Math.min(h, filtered.length - 1)));
  }, [filtered.length]);

  // Click-outside closes the dropdown (not the modal).
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!comboRef.current) return;
      if (!comboRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const mutation = useMutation<
    CreateAssetShareResponse,
    ApiError,
    CreateAssetShareRequest
  >({
    mutationFn: (body) =>
      api.post<CreateAssetShareResponse>(
        `/v1/assets/${asset.id}/share`,
        body,
      ),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: qk.assetShares(asset.id) });
      queryClient.invalidateQueries({ queryKey: qk.asset(asset.id) });
      if (res.skipped.length === 0) {
        onClose();
      }
    },
  });

  function pick(user: PublicUserDTO) {
    setSelected(user);
    setQuery(user.display_name ?? user.id.slice(0, 8));
    setOpen(false);
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!selected) return;
    mutation.mutate({ user_ids: [selected.id], role });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, Math.max(0, filtered.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && open && filtered[highlight]) {
      e.preventDefault();
      pick(filtered[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-neutral-800 bg-neutral-900 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-3 text-sm font-semibold text-neutral-100">
          Share asset
        </h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="block text-xs text-neutral-300">
            <span className="mb-1 block">Recipient</span>
            <div ref={comboRef} className="relative">
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelected(null);
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKeyDown}
                placeholder={
                  usersQuery.isLoading
                    ? 'Loading users…'
                    : 'Type a name or pick from list'
                }
                autoComplete="off"
                role="combobox"
                aria-expanded={open}
                aria-controls="share-user-listbox"
                className="w-full rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5 text-xs text-neutral-100 placeholder:text-neutral-600 focus-visible:border-neutral-500 focus-visible:outline-none"
              />
              <button
                type="button"
                aria-label="Toggle list"
                onClick={() => setOpen((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-2 text-neutral-500 hover:text-neutral-300"
              >
                ▾
              </button>
              {open && (
                <ul
                  id="share-user-listbox"
                  role="listbox"
                  className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-neutral-700 bg-neutral-950 py-1 shadow-lg"
                >
                  {usersQuery.isLoading && (
                    <li className="px-2 py-1 text-[11px] text-neutral-500">
                      Loading…
                    </li>
                  )}
                  {usersQuery.isError && (
                    <li className="px-2 py-1 text-[11px] text-red-300">
                      {usersQuery.error instanceof ApiError
                        ? usersQuery.error.reason
                        : 'Failed to load users.'}
                    </li>
                  )}
                  {usersQuery.data && filtered.length === 0 && (
                    <li className="px-2 py-1 text-[11px] text-neutral-500">
                      {query.trim()
                        ? 'No matches.'
                        : 'No other users to share with.'}
                    </li>
                  )}
                  {filtered.map((u, idx) => {
                    const isHi = idx === highlight;
                    const name = u.display_name ?? '(unnamed user)';
                    return (
                      <li
                        key={u.id}
                        role="option"
                        aria-selected={isHi}
                        onMouseEnter={() => setHighlight(idx)}
                        onMouseDown={(e) => {
                          // mousedown beats the input's blur-close race
                          e.preventDefault();
                          pick(u);
                        }}
                        className={`cursor-pointer px-2 py-1 text-xs ${
                          isHi
                            ? 'bg-neutral-800 text-neutral-50'
                            : 'text-neutral-200'
                        }`}
                      >
                        <div>{name}</div>
                        <div className="font-mono text-[10px] text-neutral-500">
                          {u.id.slice(0, 8)}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
          <label className="block text-xs text-neutral-300">
            <span className="mb-1 block">Role</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as AssetShareRole)}
              className="w-full rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5 text-xs text-neutral-100 focus-visible:border-neutral-500 focus-visible:outline-none"
            >
              <option value="viewer">Viewer</option>
              <option value="contributor">Contributor</option>
            </select>
          </label>
          {mutation.data && mutation.data.skipped.length > 0 && (
            <div className="rounded-md border border-amber-900/60 bg-amber-950/40 px-2 py-1.5 text-[11px] text-amber-200">
              Skipped {mutation.data.skipped.length}:
              <ul className="mt-1 space-y-0.5">
                {mutation.data.skipped.map((s) => (
                  <li key={s.user_id} className="font-mono">
                    {s.user_id.slice(0, 8)}… — {s.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {mutation.error && (
            <p className="text-xs text-red-300">{mutation.error.reason}</p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-neutral-700 px-3 py-1.5 text-xs text-neutral-300 hover:bg-neutral-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selected || mutation.isPending}
              className="rounded-md bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-900 hover:bg-white disabled:opacity-50"
            >
              {mutation.isPending ? 'Sharing…' : 'Share'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Kept around — useful when we get a list of share user_ids and need to
// surface display names. Returns a stable map keyed by user id.
export function useUserNames(
  userIDs: string[],
): Map<string, PublicUserDTO | null> {
  const stableIDs = useMemo(() => Array.from(new Set(userIDs)), [userIDs]);
  const results = useQueries({
    queries: stableIDs.map((id) => ({
      queryKey: ['user', id] as const,
      queryFn: () => api.get<PublicUserDTO>(`/v1/users/${id}`),
      staleTime: 5 * 60_000,
    })),
  });
  return useMemo(() => {
    const m = new Map<string, PublicUserDTO | null>();
    stableIDs.forEach((id, idx) => {
      m.set(id, (results[idx]?.data as PublicUserDTO | undefined) ?? null);
    });
    return m;
  }, [stableIDs, results]);
}
